import * as React from 'react';
import {
  X,
  MoreHorizontal,
  ImagePlus,
  User,
  Loader2,
  Pencil,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getDealOwnerConfig } from '../../lib/getDealOwnerConfig';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useDealDetail, DealDetailFull } from '../../hooks/use-deal-detail';
import DealTabbedContent from './DealTabbedContent';

interface DealDetailPanelProps {
  dealId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (dealId: string) => void;
  onChangeStage?: (dealId: string, currentStageId?: string) => void;
  onDelete?: (dealId: string) => void;
  onAddMedia?: (dealId: string) => void;
  onAITabClick?: (dealId: string, taskListId: string) => void;
  /** When true, shifts the panel left to make room for the chat panel */
  isChatOpen?: boolean;
  /** When true, the panel fills the remaining pipeline width instead of the default side-panel width */
  isExpanded?: boolean;
  /** Toggle the expanded/collapsed state */
  onToggleExpand?: () => void;
}

function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) {
    return '';
  }
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'Edited just now';
  }
  if (diffMins < 60) {
    return `Edited ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `Edited ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `Edited ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  return `Edited ${d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

export function DealDetailPanel({
  dealId,
  isOpen,
  onClose,
  onEdit,
  onChangeStage,
  onDelete,
  onAITabClick,
  isChatOpen,
  isExpanded,
  onToggleExpand,
}: DealDetailPanelProps) {
  const { dealDetail, isLoading, error, refetch } = useDealDetail(
    isOpen ? dealId : null
  );

  // The photo strip's tiles (and its "+N" overflow tile) open the Photos
  // tab below — previously the "+N" tile looked clickable but did nothing.
  // MUST be declared before the !isOpen early return: hooks have to run on
  // every render or React throws "Rendered more hooks than during the
  // previous render" the moment the panel opens (found live 2026-08-19).
  const [requestedTab, setRequestedTab] = React.useState<{
    id: string;
    nonce: number;
  } | null>(null);
  const tabbedContentRef = React.useRef<HTMLDivElement | null>(null);
  const openPhotosTab = () => {
    setRequestedTab({ id: 'photos', nonce: Date.now() });
    // Let the tab switch render, then bring the tab area into view.
    setTimeout(() => {
      tabbedContentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  if (!isOpen) {
    return null;
  }

  const renderHeader = () => (
    <div className="flex items-center justify-end gap-1 px-4 sm:px-6 py-4 border-b border-border bg-background mr-10">
      {onToggleExpand && (
        <button
          className="hidden sm:flex items-center gap-2 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-muted"
          onClick={onToggleExpand}
          aria-label={isExpanded ? 'Collapse deal view' : 'Expand deal view'}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <Minimize2 className="size-4" />
          ) : (
            <Maximize2 className="size-4" />
          )}
        </button>
      )}
      <button
        className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-muted"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="size-4" />
      </button>
    </div>
  );

  const renderLoading = () => (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <p className="text-sm text-destructive">
        {error?.message || 'Failed to load deal'}
      </p>
      <Button variant="outline" size="sm" onClick={refetch}>
        Retry
      </Button>
    </div>
  );

  const renderDealHeader = () => {
    if (!dealDetail) {
      return null;
    }

    return (
      <div className="px-4 sm:px-6 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-foreground leading-tight">
              {dealDetail.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {formatRelativeTime(dealDetail.updatedAt)}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              onClick={() => {
                if (dealDetail && onEdit) {
                  onEdit(dealDetail.id);
                }
              }}
            >
              <Pencil className="size-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="size-8 p-0">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onChangeStage && (
                  <DropdownMenuItem
                    onClick={() =>
                      onChangeStage(dealDetail.id, dealDetail.stageId)
                    }
                  >
                    Change stage
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => onDelete(dealDetail.id)}
                  >
                    Delete deal
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stage badge */}
        {dealDetail.stage && (
          <div className="mt-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              {dealDetail.stage}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderActions = () => {
    if (!dealDetail) {
      return null;
    }

    return null;
    // Actions commented out for now
    // <div className="px-4 sm:px-6 pb-4 flex items-center gap-3">
    //   <Button
    //     variant="outline"
    //     size="sm"
    //     className="gap-2"
    //     onClick={() => onAddMedia?.(dealDetail.id)}
    //   >
    //     <ImagePlus className="size-4 text-primary" />
    //     <span>Add media</span>
    //   </Button>
    // </div>
  };

  const renderPhotoStrip = () => {
    if (!dealDetail?.photos?.length) {
      return null;
    }

    const imagePhotos = dealDetail.photos.filter((p) => {
      const type = p.type || '';
      const name = p.imageName || '';
      if (type.startsWith('video/') || /\.(mp4|mov|avi|mkv)$/i.test(name)) {
        return false;
      }
      if (type === 'application/pdf' || /\.pdf$/i.test(name)) {
        return false;
      }
      if (
        type === 'application/json' ||
        type === 'report' ||
        /\.json$/i.test(name)
      ) {
        return false;
      }
      return true;
    });

    if (!imagePhotos.length) {
      return null;
    }

    return (
      <div className="px-4 sm:px-6 pb-4">
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2">
          {imagePhotos.slice(0, 6).map((photo) => (
            <div
              key={photo.id}
              onClick={openPhotosTab}
              className="shrink-0 w-[100px] h-[70px] sm:w-[140px] sm:h-[100px] rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
            >
              {photo.url ? (
                <img
                  src={photo.url}
                  alt={photo.description || 'Deal photo'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImagePlus className="size-5 sm:size-6 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
          {imagePhotos.length > 6 && (
            <div
              onClick={openPhotosTab}
              className="shrink-0 w-[100px] h-[70px] sm:w-[140px] sm:h-[100px] rounded-lg bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
            >
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                +{imagePhotos.length - 6}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMainDisplayAttrs = () => {
    if (!dealDetail?.mainDisplayAttrs?.length) {
      return null;
    }

    // Build a flat list of editable fields from mainDisplayAttrs
    // Contact-type objects get expanded into their sub-fields
    const fields: { label: string; value: string }[] = [];

    for (const attr of dealDetail.mainDisplayAttrs) {
      if (typeof attr.val === 'string' || typeof attr.val === 'number') {
        if (String(attr.val).trim() !== '') {
          fields.push({ label: attr.label, value: String(attr.val) });
        }
      } else if (
        typeof attr.val === 'object' &&
        attr.val !== null &&
        !(attr.val as any).type
      ) {
        // This is likely a Contact-type object with sub-fields
        for (const [key, subVal] of Object.entries(attr.val as any)) {
          if (
            (typeof subVal === 'string' || typeof subVal === 'number') &&
            String(subVal).trim() !== ''
          ) {
            // Convert camelCase/PascalCase key to a readable label
            const label = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (s) => s.toUpperCase())
              .trim();
            fields.push({ label, value: String(subVal) });
          }
        }
      }
    }

    if (fields.length === 0) {
      return null;
    }

    return (
      <div className="px-4 sm:px-6 pb-4 space-y-4">
        {fields.map((field, i) => (
          <div key={i} className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {field.label}
            </label>
            <p className="text-sm text-foreground">{field.value}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderPowerAttrs = () => {
    if (!dealDetail?.mainDisplayAttrs?.length) {
      return null;
    }

    const powerAttr = dealDetail.mainDisplayAttrs.find(
      (attr) =>
        typeof attr.val === 'object' && (attr.val as any)?.type === 'Power'
    );

    if (!powerAttr) {
      return null;
    }

    const val = powerAttr.val as any;

    return (
      <div className="px-4 sm:px-6 pb-4">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          {powerAttr.label}
        </h3>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Utility Company
            </label>
            <p className="text-sm text-foreground">{val.name}</p>
          </div>
          <Separator />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {val.utilityInfo}
            </label>
            {val.utilityInfo === 'Monthly Usage' && val.info ? (
              <div className="text-sm text-foreground space-y-0.5">
                <p>
                  Jan: {val.info.jan} KWs, Feb: {val.info.feb} KWs, Mar:{' '}
                  {val.info.mar} KWs
                </p>
                <p>
                  Apr: {val.info.apr} KWs, May: {val.info.may} KWs, Jun:{' '}
                  {val.info.jun} KWs
                </p>
                <p>
                  Jul: {val.info.jul} KWs, Aug: {val.info.aug} KWs, Sep:{' '}
                  {val.info.sep} KWs
                </p>
                <p>
                  Oct: {val.info.oct} KWs, Nov: {val.info.nov} KWs, Dec:{' '}
                  {val.info.dec} KWs
                </p>
              </div>
            ) : val.info != null ? (
              <p className="text-sm text-foreground">
                {val.utilityInfo === 'Annual Usage'
                  ? `${val.info} KWs`
                  : val.utilityInfo === 'Average Monthly Cost'
                  ? `${val.info} KWs/month`
                  : String(val.info)}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderKwsAttrs = () => {
    if (!dealDetail?.mainDisplayAttrs?.length) {
      return null;
    }

    const kwsAttrs = dealDetail.mainDisplayAttrs.filter(
      (attr) =>
        typeof attr.val === 'object' &&
        (attr.val as any)?.type === 'kws' &&
        (attr.val as any)?.val !== '' &&
        (attr.val as any)?.val !== '$ '
    );

    if (kwsAttrs.length === 0) {
      return null;
    }

    return (
      <div className="px-4 sm:px-6 pb-4">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Deal
        </h3>
        <div className="space-y-3">
          {kwsAttrs.map((attr, i) => (
            <div key={i} className="space-y-1">
              <label className="text-xs text-muted-foreground">
                {attr.label}
              </label>
              <p className="text-sm text-foreground">{(attr.val as any).val}</p>
              {i < kwsAttrs.length - 1 && <Separator className="mt-2" />}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOwners = () => {
    if (!dealDetail) {
      return null;
    }

    const renderAvatar = (
      user: DealDetailFull['setter'] | DealDetailFull['closer'],
      label: string
    ) => (
      <div className="flex items-center justify-between py-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          {user?.profilePicture ? (
            <img
              src={user.profilePicture}
              alt={user.name}
              className="size-7 rounded-full object-cover"
            />
          ) : user ? (
            <div className="size-7 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-[10px] font-semibold text-primary">
                {user.initials}
              </span>
            </div>
          ) : (
            <div className="size-7 rounded-full bg-muted flex items-center justify-center">
              <User className="size-3.5 text-muted-foreground" />
            </div>
          )}
          <span className="text-sm text-foreground">
            {user?.name || 'Unassigned'}
          </span>
        </div>
      </div>
    );

    return (
      <div className="px-4 sm:px-6 pb-4">
        {renderAvatar(dealDetail.setter, getDealOwnerConfig().setterLabel)}
        {!getDealOwnerConfig().hideCloser && (
          <>
            <Separator />
            {renderAvatar(dealDetail.closer, getDealOwnerConfig().closerLabel!)}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop - desktop only */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 hidden sm:block"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          'fixed top-0 md:top-10 bottom-0 left-0 sm:left-auto z-50 bg-background sm:border-l border-border shadow-xl max-w-[100vw] overflow-hidden',
          'transform transition-all duration-300 ease-in-out',
          // Width: default side-panel, or expanded to fill the remaining
          // pipeline width (minus the nav sidebar, and minus the chat panel
          // when it is open so the deal stays fully in view beside the chat).
          isExpanded
            ? isChatOpen
              ? 'w-full sm:w-[calc(100vw_-_var(--sidebar-width)_-_480px)] lg:w-[calc(100vw_-_var(--sidebar-width)_-_540px)]'
              : 'w-full sm:w-[calc(100vw_-_var(--sidebar-width))]'
            : 'w-full sm:w-[480px] lg:w-[540px]',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          isChatOpen ? 'sm:right-[480px] lg:right-[540px]' : 'right-0'
        )}
      >
        {renderHeader()}

        <div className="flex-1 overflow-y-auto overflow-x-hidden h-[calc(100%-53px)]">
          {isLoading ? (
            renderLoading()
          ) : error ? (
            renderError()
          ) : dealDetail ? (
            <>
              {renderDealHeader()}
              {renderActions()}
              {renderPhotoStrip()}
              <Separator className="mx-4 sm:mx-6" />
              <div className="py-4">
                {renderMainDisplayAttrs()}
                {renderPowerAttrs()}
                {renderKwsAttrs()}
                {renderOwners()}
              </div>
              <Separator className="mx-4 sm:mx-6" />
              <div ref={tabbedContentRef} className="px-4 sm:px-6 pt-2 pb-6">
                <DealTabbedContent
                  requestedTab={requestedTab}
                  deal={dealDetail.rawDeal}
                  dealId={dealDetail.id}
                  comments={dealDetail.comments}
                  photos={dealDetail.photos}
                  appointments={dealDetail.appointments}
                  categoryId={dealDetail.categoryId}
                  showEdit={true}
                  onTriggerSelectedDealRefetch={refetch}
                  onAITabClick={onAITabClick}
                />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              No deal selected
            </div>
          )}
        </div>
      </div>
    </>
  );
}
